import {Box, Tooltip} from "@mui/material";
import {MouseEvent, useState} from "react";
import {SURVEY_ANSWER_FONT_SIZE, SURVEY_ANSWER_LINE_HEIGHT} from "../surveySubmissionAnswerLayout";
import {useSurveyBlackoutBarPadding} from "./useSurveyBlackoutBarPadding";

/**
 * The answer typography blackout renders in — the same size and leading the read-only view uses,
 * so flipping the blackout switch does not re-typeset the answer. Owned one level up, beside the
 * question heading, because both views have to take their metrics from the same place.
 *
 * Aliased locally because the bar geometry below is DERIVED from the line-height: change it at the
 * source and the bars re-tune themselves.
 */
const ANSWER_FONT_SIZE = SURVEY_ANSWER_FONT_SIZE;

const ANSWER_LINE_HEIGHT = SURVEY_ANSWER_LINE_HEIGHT;

/**
 * Plain rendered text — never an input or a textarea.
 *
 * `pre-wrap` keeps the stored string byte-identical on screen (spaces, newlines and all), which is
 * what lets a DOM selection be read as offsets into that exact string.
 *
 * `::selection` is pinned to a default-ish blue in here on purpose: the native selection is the
 * reference a blackout bar has to match, so it must not drift with the app theme. Styling
 * `::selection` can only change its colours, never its box — the height stays the browser's own,
 * which keeps it an honest yardstick.
 */
export const surveyBlackoutTextSx = {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    cursor: "text",
    userSelect: "text",
    fontSize: ANSWER_FONT_SIZE,
    lineHeight: ANSWER_LINE_HEIGHT,
    "&::selection, & *::selection": {
        backgroundColor: "#3297fd",
        color: "#ffffff",
    },
} as const;

/**
 * The other half of the pair: an answer value a blackout can never touch — a score, a date, a chosen
 * option, an attachment, an unanswered question.
 *
 * Defined here, against `surveyBlackoutTextSx`, because the two only mean anything relative to each
 * other. The rule is "blackout covers text the guest typed", and the cursor is what states it: an
 * I-beam appears over exactly the strings a bar can be drawn on, an arrow everywhere else, so a
 * selection that could never be confirmed cannot be started in the first place.
 */
export const surveyBlackoutLockedSx = {
    color: 'text.secondary',
    userSelect: 'none',
    cursor: 'default',
} as const;

/**
 * How far the bar bleeds above and below the text's own content box.
 *
 * The browser paints the selection over the whole LINE BOX — that is why selected lines touch each
 * other with no gap — while a background only covers the content box, which is much shorter. The
 * leftover leading is split evenly above and below the content box, so matching the selection means
 * padding out by half of it:
 *
 *     (lineHeight − contentBox) / 2
 *
 * `contentBox` is the half a stylesheet cannot state: it comes from the font's own metrics, so it
 * is measured rather than guessed — see `useSurveyBlackoutBarPadding`.
 */

/** Optical bleed to the left and right, cancelled again by an equal negative margin. */
const BAR_INLINE_PADDING = "1px";

interface Props {
    /** The original characters. They stay in the DOM so selection offsets keep matching the string. */
    text: string
    /** Identifies the bar to the disarm listener, and to the caller's click handler. */
    barId: string
    /** Tooltip and accessible name. Omit for a bar that cannot be restored. */
    tooltip?: string
    armed?: boolean
    /** Staged in this session and not saved yet: drawn a shade lighter than a stored redaction. */
    pending?: boolean
    onClick?: (event: MouseEvent<HTMLSpanElement>) => void
}

/**
 * A blacked-out range: the native text selection, turned solid.
 *
 * It is an inline span that inherits every font metric from the surrounding text — no font-size, no
 * weight, no line-height of its own — so it occupies exactly the box the selection the user just
 * drew occupied. The characters stay in the DOM (hidden, not removed) so selection offsets keep
 * matching the plain string; `visibility: hidden` rather than a colour trick, because emoji ignore
 * text colour and would still show through.
 */
const SurveyBlackoutBar = ({text, barId, tooltip, armed, pending, onClick}: Props) => {
    const blockPadding = useSurveyBlackoutBarPadding(ANSWER_FONT_SIZE, ANSWER_LINE_HEIGHT);

    // The line fragment under the pointer when the hover STARTED. A wrapped bar's bounding box spans
    // both full lines, which parks a normally-anchored tooltip over the middle of the paragraph -
    // and a cursor-following tooltip moves. Capturing the hovered fragment once pins the tooltip
    // centered above it for the whole hover.
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

    const captureAnchor = (event: MouseEvent<HTMLElement>) => {
        const rects = Array.from(event.currentTarget.getClientRects());
        const hit = rects.find(rect =>
            event.clientX >= rect.left && event.clientX <= rect.right &&
            event.clientY >= rect.top && event.clientY <= rect.bottom
        );
        setAnchorRect(hit ?? rects[0] ?? null);
    };

    const bar = (
        <Box
            component={'span'}
            data-bar-id={barId}
            aria-label={tooltip}
            onClick={onClick}
            onMouseEnter={tooltip ? captureAnchor : undefined}
            sx={{
                // Armed adds an OUTLINE and nothing else: the fill stays black, so an armed bar
                // still reads as a redaction rather than as a highlight, and geometry is identical
                // in both states — arming never nudges a character. Outline (not border) because it
                // is painted outside the box model and takes no space.
                bgcolor: pending ? 'grey.700' : 'text.primary',
                outline: armed ? '2px solid' : 'none',
                outlineColor: 'warning.dark',
                // A 1px breathing gap between the black fill and the red frame, so the outline reads
                // as a frame around the bar rather than a red edge of it. The 3px total overhang
                // reaches into the neighbouring line box, but glyphs only occupy its centre, so
                // nothing legible is ever covered.
                outlineOffset: '1px',
                // Selection-shaped, not pill-shaped: just enough to take the hard corner off.
                borderRadius: '2px',
                cursor: onClick ? 'pointer' : 'default',

                // WHY vertical padding rather than a taller box: on a non-replaced INLINE element,
                // padding in the block direction paints the background taller but takes no part in
                // the line-box calculation (CSS 2.1 §10.6.1 — the line box comes from font metrics
                // and line-height alone). So the bar grows to the height of the browser's own
                // selection highlight with ZERO layout shift: no line moves, no character reflows,
                // nothing below the bar jumps. Setting a height, or going inline-block, would do
                // exactly the opposite.
                paddingBlock: blockPadding,

                // Horizontally the bar must cover the same advance widths the selection did. The 1px
                // is optical only — it closes the hairline between the glyph cell and the ink — and
                // the matching negative margin gives it back, so the run of text keeps its exact
                // width.
                paddingInline: BAR_INLINE_PADDING,
                marginInline: `-${BAR_INLINE_PADDING}`,

                // A bar that wraps gets the full background box on every line fragment.
                boxDecorationBreak: 'clone',
                WebkitBoxDecorationBreak: 'clone',
            }}
        >
            <Box component={'span'} sx={{visibility: 'hidden'}}>{text}</Box>
        </Box>
    );

    if (!tooltip) return bar;

    return (
        <Tooltip
            title={tooltip}
            arrow
            placement={'top'}
            PopperProps={anchorRect
                ? {anchorEl: {getBoundingClientRect: () => anchorRect}}
                : undefined}
        >
            {bar}
        </Tooltip>
    );
};

export default SurveyBlackoutBar;
